import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { PageHeader } from "@/components/layout/PageHeader";
import { StatusPill } from "@/components/shared/StatusPill";
import { BusinessStage } from "@/components/shared/BusinessStage";
import { Loading } from "@/components/shared/Loading";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useProfitability } from "@/api/hooks";
import { api } from "@/api/client";
import { apiErrorMessage } from "@/lib/apiError";
import { money } from "@/lib/format";
import { toast } from "sonner";
import { AlertCircle, CheckCircle2, LockKeyhole, Loader2 } from "lucide-react";

const normalizedStatus = (value) => String(value || "").trim().toUpperCase().replaceAll("_", " ");
const blockerMessage = (blocker) => typeof blocker === "string" ? blocker : blocker?.message || blocker?.code || "A required closure control is incomplete.";
const blockersFor = (job) => {
  const source = Array.isArray(job?.blockers) ? job.blockers : Array.isArray(job?.closureBlockers) ? job.closureBlockers : [];
  return source.map(blockerMessage);
};

export default function FinanceProfitability() {
  const { data: rows, error } = useProfitability();
  const qc = useQueryClient();
  const [closingJob, setClosingJob] = useState(null);
  const [closing, setClosing] = useState(false);

  if (error) return <div className="p-6 text-slate-500">Profitability requires cost visibility.</div>;
  if (!rows) return <Loading />;

  const closeJob = async () => {
    if (!closingJob) return;
    setClosing(true);
    try {
      await api.post(`/finance/jobs/${closingJob.id}/close`, { expectedVersion: Number(closingJob.version ?? 0) });
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["profit"] }),
        qc.invalidateQueries({ queryKey: ["jobs"] }),
        qc.invalidateQueries({ queryKey: ["customers"] }),
      ]);
      toast.success(`Job ${closingJob.id} financially closed`);
      setClosingJob(null);
    } catch (e) {
      toast.error(apiErrorMessage(e, "The job could not be closed."));
    } finally {
      setClosing(false);
    }
  };

  return (
    <>
      <PageHeader title="Profitability & Closure" subtitle="Job margin review and controlled financial closure" />
      <div className="flex-1 overflow-y-auto p-6">
        <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1220px] text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
                  <th className="text-left font-semibold px-4 py-2.5">Job / customer</th>
                  <th className="text-left font-semibold px-4 py-2.5">Current stage / last status</th>
                  <th className="text-right font-semibold px-4 py-2.5">Revenue</th>
                  <th className="text-right font-semibold px-4 py-2.5">Cost</th>
                  <th className="text-right font-semibold px-4 py-2.5">Gross profit</th>
                  <th className="text-right font-semibold px-4 py-2.5">GP %</th>
                  <th className="text-left font-semibold px-4 py-2.5">Closure control</th>
                  <th className="text-right font-semibold px-4 py-2.5">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((j) => {
                  const blockers = blockersFor(j);
                  const closed = ["CLOSED", "FINANCIALLY CLOSED"].includes(normalizedStatus(j.status));
                  const eligible = j.closureEligible === true && !closed;
                  const gpPositive = Number(j.gp) >= 0;
                  return (
                    <tr key={j.id} data-testid={`profit-${j.id}`} className="align-top hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-mono-num text-[13px] font-semibold text-slate-900">{j.id}</div>
                        <div className="text-xs text-slate-500">{j.customer}</div>
                        <div className="mt-1"><StatusPill status={j.status} /></div>
                      </td>
                      <td className="px-4 py-3 min-w-[250px]"><BusinessStage stage={j.businessStage} /></td>
                      <td className="px-4 py-3 text-right font-mono-num text-slate-700">{money(j.revenue, j.currency || "AED")}</td>
                      <td className="px-4 py-3 text-right font-mono-num text-slate-600">{money(j.cost, j.currency || "AED")}</td>
                      <td className={`px-4 py-3 text-right font-mono-num font-semibold ${gpPositive ? "text-emerald-700" : "text-red-700"}`}>{money(j.gp, j.currency || "AED")}</td>
                      <td className={`px-4 py-3 text-right font-mono-num font-semibold ${gpPositive ? "text-slate-900" : "text-red-700"}`}>{j.pct}%</td>
                      <td className="px-4 py-3 min-w-[260px]">
                        {closed ? (
                          <div className="flex items-center gap-1.5 text-xs font-medium text-slate-600"><LockKeyhole className="h-3.5 w-3.5" />Financially closed</div>
                        ) : eligible ? (
                          <div className="flex items-center gap-1.5 text-xs font-medium text-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />All closure controls complete</div>
                        ) : (
                          <div className="space-y-1" data-testid={`closure-blockers-${j.id}`}>
                            {(blockers.length ? blockers : ["Closure eligibility is still being verified."]).map((message, index) => (
                              <div key={`${message}-${index}`} className="flex items-start gap-1.5 text-xs text-amber-800"><AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{message}</span></div>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {eligible && <Button size="sm" onClick={() => setClosingJob(j)} data-testid={`close-job-${j.id}`}><LockKeyhole className="h-3.5 w-3.5" />Close job</Button>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {rows.length === 0 && <div className="p-8 text-center text-sm text-slate-500">No jobs are available for profitability review.</div>}
        </div>
      </div>

      <Dialog open={!!closingJob} onOpenChange={(open) => { if (!open && !closing) setClosingJob(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="close-job-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><LockKeyhole className="h-4 w-4 text-blue-800" />Confirm financial closure</DialogTitle>
            <DialogDescription>{closingJob ? `${closingJob.id} · ${closingJob.customer}` : ""}</DialogDescription>
          </DialogHeader>
          {closingJob && (
            <div className="space-y-4">
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                Revenue {money(closingJob.revenue, closingJob.currency || "AED")} · Cost {money(closingJob.cost, closingJob.currency || "AED")} · Gross profit {money(closingJob.gp, closingJob.currency || "AED")}
              </div>
              <p className="text-sm leading-6 text-slate-600">This confirms that required documents, customer receipts and supplier payments are complete. The job will move to final closure.</p>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setClosingJob(null)} disabled={closing}>Cancel</Button>
                <Button onClick={closeJob} disabled={closing} data-testid="close-job-confirm">
                  {closing && <Loader2 className="h-4 w-4 animate-spin" />}Confirm closure
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
